// import Cookies from 'universal-cookie';
import React, { useEffect, useState } from "react";

import { BrowserRouter } from "react-router-dom";
import ContentPanel from "./Router/ContentPanel";

import { useDispatch } from "react-redux";
import "@cdssnc/gcds-components-react/gcds.css";

import {
  authenticateToken,
  getApplicationStatus,
  getWorklocationsFromApi,
} from "./util/ApiCalls";

import "./App.css";
import {
  applicationType,
  portWorklocations,
  programType,
} from "./Redux/SetPortWorklocations";
import { ConsoleLogger } from "./Logger/Logger";
import { Loader } from "./Components/Loading/Loader";
import Maintenance from "./Containers/Maintenance/Maintenance";
import { useTranslation } from "react-i18next";
import { getMessageDelayTime, removeLocalStorage } from "./util/Function/Countdown";
import CountdownModal from "./Components/CountdownModal/CountdownModal";
import { version as uuidVersion, validate as uuidValidate } from 'uuid';
import { checkToken } from "./util/validationUtil";

const logger = new ConsoleLogger(App);

// ENUM
const AllowedStates = {
  AUTHORIZED: 1,
  UNAUTHORIZED: 2,
  UNDEFINED: -1,
};

function App() {
  const dispatch = useDispatch();
  const [allowed, setAllowed] = useState(AllowedStates.UNDEFINED);
  const [applicationStatus, setApplicationStatus] = useState(false);
  const [applicationStatusText, setApplicationStatusText] = useState({});
  const [showCountdownModal, setShowCountdownModal] = useState(false);
  const { t } = useTranslation();

  /**
   * Route base path to /ttp
   */
  if (window.location.pathname === "/") {
    window.location.pathname = "/ttp";
  }

  /**
   * Save codes to dispatch
   * @param locations
   */
  const storeCodes = (locations) => {
    dispatch(portWorklocations(locations.data.codes[0].codes));
    dispatch(programType(locations.data.codes[1].codes));
    dispatch(applicationType(locations.data.codes[2].codes));
  };

  /**
   * Default to empty list if failed
   */
  const failStoreCodes = () => {
    dispatch(portWorklocations([]));
    dispatch(programType([]));
    dispatch(applicationType([]));
  };

  /**
   * Get the codes the UI calls
   * @returns {Promise<void>}
   */
  const getCodes = async () => {
    let locationResults = await getWorklocationsFromApi();

    if (locationResults?.status === 200) {
      storeCodes(locationResults);
    } else {
      failStoreCodes();
    }
  };

const checkUUID = (token) => {
  const localUUID = localStorage.getItem("myAccessId");
  const storedToken = localStorage.getItem("myToken");

  const isLocal = window.location.origin.startsWith("http://localhost");

  // Local development
  if (isLocal) {
    if (checkToken(token)) {
      localStorage.setItem("myToken", token);
      return false;
    }

    if (uuidValidate(token)) {
      localStorage.setItem("myAccessId", token);
      return false;
    }

    return false;
  }

  // UUID already stored, must also have matching token
  if (localUUID === token) {
    return storedToken === token;
  }

  // New UUID, validate it
  return uuidValidate(token);
};

  const setTimeoutHandlers = () => {
    let expiryTimeMs = localStorage.getItem("expiryTimeMs")


    if (expiryTimeMs) {
      const EXPIRED_SESSION_MINUTES = 1 * 60 * 1000;
      const removeLSDelay = expiryTimeMs - EXPIRED_SESSION_MINUTES
      const expireTime = removeLSDelay - Date.now()

      setTimeout(() => {
        if (
          expireTime > 0
        ) {
          localStorage.removeItem("myToken");
          localStorage.removeItem("expiryTimeMs");
          localStorage.removeItem("myAccessId");
          localStorage.removeItem("mytoken");
        }
      }, expireTime)

      // Get the 10 minute delay based on the user's expirary token time
      const delay = getMessageDelayTime();
      setTimeout(() => {
        if (
          delay > 0 &&
          allowed === AllowedStates.AUTHORIZED
        ) {
          setShowCountdownModal(true);
        }
      }, delay);
    }
  }

  /**
   * Call the backend to verify the authorization token
   * @returns {Promise<void>}
   */
  const checkTokenAuthorizations = async () => {
    let expireTime
    try {
      logger.log("Verifying the user token");
      const resultOfToken = await authenticateToken();
      // Save the user's token expirary time
      if (resultOfToken) {
        //unix time
        const expirySeconds = resultOfToken.data.exp;
        const expiryTimeMs = expirySeconds * 1000;
        localStorage.setItem("expiryTimeMs", expiryTimeMs.toString());
        // localStorage.setItem("expiryTimeMs", 1757360783 * 1000).toString(); // DEV TEST
      }

      // setAllowed(resultOfToken.status === GE_RES_CODES.OK)
      logger.log("Verified the user token");
      setAllowed(
        resultOfToken.status === 200
          ? AllowedStates.AUTHORIZED
          : AllowedStates.UNAUTHORIZED
      );
    } catch (err) {
      setAllowed(AllowedStates.UNAUTHORIZED);
    }
  };

  const checkApplicationStatus = async () => {
    //api call here
    let appStatus = await getApplicationStatus();

    if (appStatus) {
      if (appStatus.data?.status === "online") {
        setApplicationStatus(false);
      } else {
        setApplicationStatus(true);
        setApplicationStatusText(appStatus.data.message);
      }
    }
  };

  useEffect(() => {
    /**
     * Call the current window or dev1 if it is local
     */
    const searchParams = new URLSearchParams(document.location.search)
    let token = searchParams.get("token");
    let redirect = searchParams.get(
      "redirect"
    );

    //check if our uuid is in our localstorage
    //if we already used the same token it will return null
    let validUUID = checkUUID(token)
    if (validUUID) {

      //remove old token, and add new uuid
      localStorage.removeItem("myToken");
      localStorage.removeItem("expiryTimeMs");
      localStorage.removeItem("myAccessId");
      localStorage.removeItem("mytoken");


      localStorage.setItem("myAccessId", token);

      // save redirect
      if (redirect) {
        localStorage.setItem("user-redirect", redirect);
      }

      // begin auth check
      checkTokenAuthorizations();
      searchParams.delete("token")
      searchParams.delete("redirect")
      logger.log("Application is starting.");
    } else {

      //check if we have a token + when it expires.
      const storedToken = localStorage.getItem('myToken')
      //has token
      if (storedToken) {
        if (window.location.pathname === "/ttp/unauthorized") {
          setAllowed(AllowedStates.UNAUTHORIZED)
        } else {
          setAllowed(AllowedStates.AUTHORIZED)
        }
      } else {
        setAllowed(AllowedStates.UNAUTHORIZED)
      }
    }
  }, []);

  useEffect(() => {



    if (allowed === AllowedStates.UNDEFINED) {
      //do nothing
    } else if (allowed === AllowedStates.AUTHORIZED) {
      getCodes();
      checkApplicationStatus();
      setTimeoutHandlers()
    } else if (allowed === AllowedStates.UNAUTHORIZED) {
      if (window.location.pathname !== "/ttp/unauthorized") {
        window.location.pathname = "/ttp/unauthorized";
      }
    }
  }, [allowed]);

  const contentInRouter = <ContentPanel />;

  return (
    <div className="App">
      {showCountdownModal ? (
        <CountdownModal
          showCountdownModal={showCountdownModal}
          setShowCountdownModal={setShowCountdownModal}
        />
      ) : null}
      {applicationStatus ? (
        <BrowserRouter>
          {<Maintenance applicationStatusText={applicationStatusText} />}
        </BrowserRouter>
      ) : (
        <Loader
          loadingLabel={t("global.loading")}
          content={
            <BrowserRouter basename={"/ttp"}>{contentInRouter}</BrowserRouter>
          }
          loading={allowed === AllowedStates.UNDEFINED}
        />
      )}
    </div>
  );
}

export default App;
